import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { FamilyContext } from '../Context/FamilyHeadContext'
import manPic from '../Image/userM.png'
import womanPic from '../Image/userW.png'
import MemorialIcon from "../pages/PersonDetailes/hasMemorialTitle"
import { API_URL, doApiGet } from '../services/apiService'
import { formatFullName } from '../utils/personUtils'

const TodayEvents = () => {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const scrollRef = useRef(null)
  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const {
    processPerson,
    isMainSite,
    currentFamilyHead,
  } = useContext(FamilyContext)
    const {t, i18n} = useTranslation()

  const isLikelyAlive = (person) => {
    if (person.death?.date) return true
    if (!person.birth?.date) return true

    const birthDate = person.birth.date
    if (/[a-zA-Z]/.test(birthDate)) return true

    const parts = birthDate.split('/')
    if (parts.length === 1) {
      const birthYear = parseInt(parts[0])
      return !birthYear || new Date().getFullYear() - birthYear <= 110
    }

    if (parts.length === 3) {
      const [day, month, year] = parts.map(Number)
      if (!year) return true
      const parsedBirthDate = new Date(year, month - 1, day)
      const age =
        (new Date() - parsedBirthDate) / (1000 * 60 * 60 * 24 * 365.25)
      return age <= 110
    }

    return true
  }

  useEffect(() => {
    const fetchTodayEvents = async () => {
        setLoading(true); // Set loading to true at the beginning
        try {
            const data = await doApiGet(`${API_URL}/today/${isMainSite}`);

            // Get today's date in DD/MM/YYYY format for comparison
            const today = new Date();
            const todayFormatted = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;

            const isPersonRelevant = (person) => {
              if (!person.connections || person.connections.length === 0) {
                return false;
              }
              const relevantConnections = [currentFamilyHead.gedId];
              const result = person.connections.some(connection =>
                  connection && relevantConnections.includes(connection)
              );
              return result;
            };
            const formattedEvents = [
                ...(data.resentDeaths?.filter(isPersonRelevant).map((person) => {
                    const processedPerson = processPerson(person);
                    const isTodayDeath = person.death?.date === todayFormatted;
                    return {
                        id: processedPerson.gedId,
                        type: 'recentDeath',
                        isTodayDeath: isTodayDeath, 
                        name: processedPerson.name,
                        familyName: processedPerson.familyName,
                        link: processedPerson.path,
                        date: person.death,
                        images: person.images,
                        sex: processedPerson.sex,
                    };
                }) || []),
                ...(data.diedToday?.filter(isPersonRelevant).map((person) => {
                    const processedPerson = processPerson(person);
                    return {
                        id: processedPerson.gedId,
                        type: 'פטירה', // Yahrzeit type
                        name: processedPerson.name,
                        familyName: processedPerson.familyName,
                        link: processedPerson.path,
                        date: person.death,
                        images: person.images,
                        sex: processedPerson.sex,
                    };
                }) || []),
                ...(data.bornToday
                    ?.filter(
                        (person) => isPersonRelevant(person) && isLikelyAlive(person),
                    )
                    .map((person) => {
                        const processedPerson = processPerson(person);
                        return {
                            id: processedPerson.gedId,
                            type: 'לידה',
                            name: processedPerson.name,
                            familyName: processedPerson.familyName,
                            link: processedPerson.path,
                            images: person.images,
                            sex: processedPerson.sex,
                        };
                    }) || []),
            ];

            setEvents(formattedEvents);
        } catch (err) {
            console.error("Error fetching today's events:", err);
        } finally {
            setLoading(false);
        }
    };

    fetchTodayEvents();
}, [
    isMainSite,
    processPerson,
    currentFamilyHead,
]);

    const calculateYearsSincePassing = (event) => {
        // 1. הגנות: אם אין אירוע, או שזה לא יארצייט, או שאין תאריך - צא
        if (!event || event.type !== 'פטירה' || !event.date?.date) return null;

        const dateStr = event.date.date.toString();

        // 2. חילוץ שנה בשיטה חסינה (Regex)
        // זה יעבוד גם ל- "9/3/1913", גם ל- "1913", וגם ל- "ABT 1913"
        const yearMatch = dateStr.match(/\d{4}/);
        if (!yearMatch) return null;

        const deathYear = parseInt(yearMatch[0], 10);
        const currentYear = new Date().getFullYear();

        // 3. חישוב פשוט: שנה נוכחית פחות שנת הפטירה
        // (מכיוון שהשרת כבר קבע שהיארצייט הוא היום, לא מעניין אותנו החודש הלועזי)
        const yearsDiff = currentYear - deathYear;

        return yearsDiff > 0 ? yearsDiff : 0;
    }

  useEffect(() => {
      if (loading || events.length <= 1) return

    const scrollContainer = scrollRef.current
    const container = containerRef.current
    const content = contentRef.current

    if (!scrollContainer || !container || !content) return

    const clone = content.cloneNode(true)
    scrollContainer.appendChild(clone)

    let scrollPosition = 0
    const scrollSpeed = 3
    const pauseDuration = 15
    let isPaused = false
    let pauseTimeout

    const scroll = () => {
      if (!isPaused && scrollContainer) {
        scrollPosition += scrollSpeed
        if (scrollPosition >= content.offsetHeight) {
          scrollPosition = 0
          isPaused = true
          clearTimeout(pauseTimeout)
          pauseTimeout = setTimeout(() => {
            isPaused = false
          }, pauseDuration)
        }
        scrollContainer.scrollTop = scrollPosition
      }
    }
    const handleTouchStart = () => {
      isPaused = true
      clearTimeout(pauseTimeout)
    }

    const handleTouchEnd = () => {
      pauseTimeout = setTimeout(() => {
        isPaused = false
      }, 1000) 
    }
    const scrollInterval = setInterval(scroll, 50)
    const handleMouseEnter = () => {
      isPaused = true
      clearTimeout(pauseTimeout)
    }
    const handleMouseLeave = () => (isPaused = false)

    container.addEventListener('mouseenter', handleMouseEnter)
    container.addEventListener('mouseleave', handleMouseLeave)
    container.addEventListener('touchstart', handleTouchStart, {passive: true})
    container.addEventListener('touchend', handleTouchEnd)

    return () => {
      clearInterval(scrollInterval)
      clearTimeout(pauseTimeout)
      if (clone?.parentNode) clone.parentNode.removeChild(clone)
      container.removeEventListener('mouseenter', handleMouseEnter)
      container.removeEventListener('mouseleave', handleMouseLeave)
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [events, loading])

  // Group events by type
  const groupedEvents = useMemo(() => {
    return events.reduce((acc, event) => {
      if (!acc[event.type]) acc[event.type] = []
      acc[event.type].push(event)
      return acc
    }, {})
  }, [events])
  const groupOrder = ['recentDeath', 'פטירה', 'לידה']
  const headerMap = {
    recentDeath: t('todayEvents.recentMemorial'), 
    פטירה: t('todayEvents.memorial'),
    לידה: t('todayEvents.birthdays'),
  }

  if (loading) {
    return (
      <div className="text-center p-4 font-heebo">
        {t('todayEvents.loading')}
      </div>
    )
  }
  if (!events.length) {
    return (
      <div className="text-center p-4 font-heebo">
        {t('todayEvents.noEvents')}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative h-[300px] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-white to-transparent z-10" />
      <div ref={scrollRef} className="h-full overflow-y-hidden">
        <div ref={contentRef} className="space-y-6 py-8">
          {groupOrder.map((eventType) => {
            const groupEvents = groupedEvents[eventType]
            if (!groupEvents || groupEvents.length === 0) return null
            return (
              <div key={eventType} className="space-y-4">
                <h4 className="text-xl font-heebo font-bold text-[#937c5d]">
                  {headerMap[eventType]}
                </h4>
                <div className="space-y-3">
                  {groupEvents.map((event) => {
                  
                    return (
                    <Link
                      key={event.id}
                      to={event.link}
                      className={`block p-4 rounded-xl transition-all duration-300 
                        ${
                          event.type === 'לידה'
                            ? 'bg-gradient-to-r from-[#937c5d]/10 to-[#c9a66c]/10 hover:from-[#937c5d]/20 hover:to-[#c9a66c]/20'
                            : 'bg-gradient-to-r from-gray-100 to-gray-50 hover:from-gray-200 hover:to-gray-100'
                        } 
                        shadow-sm hover:shadow-md`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Profile Image */}
                        <div
                            className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-white border border-[#e6dfd4]">
                          {event.images?.length > 0 ? (
                              <img
                                  crossOrigin="use-credentials"
                                  src={`${API_URL}/image/download/${encodeURIComponent(
                                      event.images[0],
                                  )}`}
                                  alt={`${formatFullName(event, i18n['language'])}`}
                                  className="w-full h-full object-cover"
                              />
                          ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {event.sex === 'M' ? (
                                    <img crossOrigin="use-credentials" src={manPic} alt="תמונת גבר"/>
                                ) : (
                                    <img crossOrigin="use-credentials" src={womanPic} alt="תמונת אישה"/>
                                )}
                              </div>
                          )}
                        </div>
                        {/* Content */}
                        <div className="flex flex-col gap-1">
                            <div className="text-lg font-heebo text-[#4a3f30]">
                                {/* --- Conditional Text Logic --- */}

                                {/* Birthday */}
                                {event.type === 'לידה' && (
                                    <>
                                        {t('todayEvents.birthdayPrefix')}{' '}
                                        {formatFullName(event, i18n['language'])}
                                    </>
                                )}

                                {/* Yahrzeit */}
                                {event.type === 'פטירה' && (
                                    <>
                                        {t('todayEvents.memorialPrefix')}{' '}
                                        {formatFullName(event, i18n['language'])}
                                        <MemorialIcon person={event}/>
                                    </>
                                )}

                                {/* Recent Death - Died Today */}
                                {event.type === 'recentDeath' && event.isTodayDeath && (
                                    <>
                                        {t('todayEvents.bdePrefix') /* ברוך דיין האמת */}
                                        {' '}
                                        {t('todayEvents.diedTodayPrefix') /* היום */}
                                        {' '}
                                        {event.sex === 'F' ? t('todayEvents.femalePassedSuffixBDE') : t('todayEvents.malePassedSuffixBDE') /* נפטרה/נפטר */}
                                        {' '}
                                        {formatFullName(event, i18n['language'])}
                                        <MemorialIcon person={event}/>
                                    </>
                                )}

                                {/* Recent Death - Died This Week (not today) */}
                                {event.type === 'recentDeath' && !event.isTodayDeath && (
                                    <>
                                        {t('todayEvents.bdePrefix') /* ברוך דיין האמת */}
                                        {' '}
                                        {formatFullName(event, i18n['language'])}
                                        {' '}
                                        {event.sex === 'F' ? t('todayEvents.femalePassedSuffixBDE') : t('todayEvents.malePassedSuffixBDE') /* נפטרה/נפטר */}
                                        {' '}
                                        {t('todayEvents.thisWeekSuffix')} {/* השבוע */}
                                        {event.date?.dateHebrew && ` (${event.date.dateHebrew})`} {/* (תאריך עברי) */}
                                        <MemorialIcon person={event}/>
                                    </>
                                )}
                                {/* --- End of Conditional Text Logic --- */}
                            </div>

                            {/* --- Conditional Subtext (Years Since Passing) --- */}
                            {/* Show only for Yahrzeit (פטירה), not for recentDeath */}
                            {event.type === 'פטירה' && (
                                <div className="text-sm text-[#937c5d]">
                                    {calculateYearsSincePassing(event)}{' '}
                                    {t('todayEvents.yearsPrefix')}{' '}
                                    {event.sex === 'F'
                                        ? t('todayEvents.femalePassingSuffixYahrzeit') 
                                        : t('todayEvents.malePassingSuffixYahrzeit')} 
                                </div>
                            )}
                            {/* --- End of Conditional Subtext --- */}
                        </div>
                      </div>
                    </Link>
                  );
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent z-10"/>
    </div>
  )
}

export default TodayEvents
